import { auth } from '@/lib/api';
import { signUp } from '@/lib/auth-client';
import { useDebounce } from '@/lib/hooks/use-debounce';
import { routes } from '@/lib/routes';
import { Check, Globe, Loader2, Lock, Mail, User, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';

const MARKETING_URL = import.meta.env.VITE_MARKETING_URL || 'https://www.caneta.ink';

const COUNTRIES = [
  { code: 'PT', name: 'Portugal' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'BR', name: 'Brazil' },
  { code: 'JP', name: 'Japan' },
  { code: 'AU', name: 'Australia' },
  { code: 'CA', name: 'Canada' }
];

export default function RegisterPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  // Username availability check
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const debouncedUsername = useDebounce(username, 500);

  useEffect(() => {
    if (debouncedUsername.length < 3) {
      setUsernameAvailable(null);
      return;
    }

    const checkUsername = async () => {
      setCheckingUsername(true);
      try {
        const { available } = await auth.usernameAvailable(debouncedUsername);
        setUsernameAvailable(available);
      } catch {
        setUsernameAvailable(null);
      } finally {
        setCheckingUsername(false);
      }
    };

    checkUsername();
  }, [debouncedUsername]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (usernameAvailable === false) {
      setError(t('auth.usernameTaken'));
      return;
    }

    setLoading(true);

    try {
      await signUp.email({
        email,
        password,
        username: username.toLowerCase(),
        country,
        privacyPolicyVersion: '2026-02-05'
      });

      setRegistered(true);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (registered) {
    return (
      <div className="space-y-6 text-center">
        <Mail className="w-12 h-12 text-sage mx-auto" />
        <h2 className="text-2xl font-medium text-ink">{t('auth.verifyEmail')}</h2>
        <p className="text-sepia">{t('auth.verifyEmailDesc')}</p>
        <p className="text-sm text-sepia/70">
          {t('auth.hasAccount')}{' '}
          <Link to={routes.login} className="text-blue hover:underline font-medium">
            {t('auth.signIn')}
          </Link>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-2xl font-medium text-ink text-center">{t('auth.createAccount')}</h2>

      {error && (
        <div className="bg-red/10 border border-red/20 text-red rounded-md p-3 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-sepia mb-1.5">
            {t('auth.email')}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-warm" />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full pl-10 pr-4 py-2.5 border border-gray-warm rounded-md focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
              placeholder="you@example.com"
            />
          </div>
        </div>

        <div>
          <label htmlFor="username" className="block text-sm font-medium text-sepia mb-1.5">
            {t('auth.username')}
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-warm" />
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              maxLength={20}
              pattern="[a-zA-Z0-9_]+"
              className="w-full pl-10 pr-10 py-2.5 border border-gray-warm rounded-md focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
              placeholder={t('auth.username')}
            />
            {username.length >= 3 && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {checkingUsername ? (
                  <Loader2 className="w-5 h-5 text-gray-warm animate-spin" />
                ) : usernameAvailable === true ? (
                  <Check className="w-5 h-5 text-sage" />
                ) : usernameAvailable === false ? (
                  <X className="w-5 h-5 text-red" />
                ) : null}
              </div>
            )}
          </div>
          {usernameAvailable === false && (
            <p className="text-red text-xs mt-1">{t('auth.usernameTaken')}</p>
          )}
          {usernameAvailable === true && (
            <p className="text-sage text-xs mt-1">{t('auth.usernameAvailable')}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-sepia mb-1.5">
            {t('auth.password')}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-warm" />
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-warm rounded-md focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
              placeholder={t('auth.weakPassword')}
            />
          </div>
        </div>

        <div>
          <label htmlFor="country" className="block text-sm font-medium text-sepia mb-1.5">
            {t('auth.country')}
          </label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-warm" />
            <select
              id="country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              required
              className="w-full pl-10 pr-4 py-2.5 border border-gray-warm rounded-md focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all appearance-none bg-white"
            >
              <option value="">{t('auth.country')}</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <input
          id="consent"
          type="checkbox"
          checked={consentAccepted}
          onChange={(e) => setConsentAccepted(e.target.checked)}
          className="mt-1 w-4 h-4 rounded border-gray-warm text-blue focus:ring-blue/30"
        />
        <label htmlFor="consent" className="text-sm text-sepia leading-snug">
          <Trans
            i18nKey="auth.consentLabel"
            components={{
              privacy: (
                // biome-ignore lint/a11y/useAnchorContent: Trans injects children
                <a
                  href={`${MARKETING_URL}/en/privacy`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue hover:underline font-medium"
                />
              ),
              terms: (
                // biome-ignore lint/a11y/useAnchorContent: Trans injects children
                <a
                  href={`${MARKETING_URL}/en/terms`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue hover:underline font-medium"
                />
              )
            }}
          />
        </label>
      </div>

      <button
        type="submit"
        disabled={loading || usernameAvailable === false || !consentAccepted}
        className="w-full py-2.5 bg-ink text-cream rounded-md font-medium hover:bg-ink/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('common.loading')}
          </>
        ) : (
          t('auth.signUp')
        )}
      </button>

      <p className="text-center text-sm text-sepia">
        {t('auth.hasAccount')}{' '}
        <Link to={routes.login} className="text-blue hover:underline font-medium">
          {t('auth.signIn')}
        </Link>
      </p>
    </form>
  );
}
